import { useState, useEffect } from "react";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { Container, Form, Button, Row, Col, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { getAllBankStatements } from "../Api/BankStatements";
import { getAllCurrencies } from "../Api/CurrencyMaster";
import { fetchACHDData, fetchACITDataByDocno } from "../Api/AchdDataWithUTR";
import { saveReconciliationDocument } from "../Api/Reconciliation";

const ReconciliationDocument = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;

  const [reconciliationData, setReconciliationData] = useState({
    statementId: "",
    docno: "",
    docfy: "",
    recoAmount: "",
    recoCurrency: "",
    transferFromCompanyCode: "",
    transferToCompanyCode: "",
    remarks: "",
    head1: "",
    head2: "",
    head3: "",
  });

  const [companyCodes, setCompanyCodes] = useState([]);
  const [bankStatements, setBankStatements] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [achdData, setAchdData] = useState([]);
  const [recoAmounts, setRecoAmounts] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const ccResponse = await getAllCompanyCodes();
        setCompanyCodes(ccResponse || []);

        const bsResponse = await getAllBankStatements();
        setBankStatements(bsResponse || []);

        const currencyResponse = await getAllCurrencies();
        setCurrencies(currencyResponse || []);

        const achdResponse = await fetchACHDData();
        setAchdData(achdResponse || []);
      } catch (error) {
        toast.error("Failed to load data");
      }
    };
    loadData();
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "docno") {
      const selectedItem = achdData.find((item) => item.docno === value);

      try {
        const acitData = await fetchACITDataByDocno(value);

        const amountsArray = Array.isArray(acitData)
          ? acitData.map((item) => item.amount)
          : [acitData.amount];

        setRecoAmounts(amountsArray || []);

        setReconciliationData((prev) => ({
          ...prev,
          docno: value,
          docfy: selectedItem?.docfy || "",
          transferFromCompanyCode:
            selectedItem?.transferFromCompanyCode || selectedItem?.doccc || "",
          transferToCompanyCode:
            selectedItem?.transferToCompanyCode || selectedItem?.doccc || "",
          recoAmount: "",
        }));
      } catch (err) {
        toast.error("Failed to fetch ACIT data");
      }
    } else {
      setReconciliationData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Sending data:", reconciliationData);

    const stringData = Object.fromEntries(
      Object.entries(reconciliationData).map(([key, value]) => [
        key,
        String(value),
      ])
    );

    setLoading(true);
    try {
      await saveReconciliationDocument(stringData);
      toast.success("🦄 Reconciliation Document Added Successfully");

      setReconciliationData({
        statementId: "",
        docno: "",
        docfy: "",
        recoAmount: "",
        recoCurrency: "",
        transferFromCompanyCode: "",
        transferToCompanyCode: "",
        remarks: "",
        head1: "",
        head2: "",
        head3: "",
      });

      navigate("/viewReconciliationDocuments");
    } catch (error) {
      toast.error(error.message || "🦄 Some Error Happened");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container asset-master-fields">
        <h2 className="fields-title">Reconciliation Document</h2>

        <Form onSubmit={handleSubmit} className="fields-form">

          <Accordion defaultActiveKey="0">

            <Accordion.Item eventKey="0">
              <Accordion.Body>

                <Row className="g-3">

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Statement ID <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="statementId"
                        value={reconciliationData.statementId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Statement</option>
                        {bankStatements.map((statement) => (
                          <option
                            key={statement.statementId}
                            value={statement.statementId}
                          >
                            {statement.statementId} - {statement.bankName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Document Number <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="docno"
                        value={reconciliationData.docno}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Document Number</option>
                        {achdData.map((item) => (
                          <option key={item.id} value={item.docno}>
                            {item.docno}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Document Fiscal Year <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="docfy"
                        value={reconciliationData.docfy}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Fiscal Year</option>
                        {achdData.map((item) => (
                          <option key={`${item.id}-${item.docfy}`} value={item.docfy}>
                            {item.docfy}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Reconciliation Amount <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="recoAmount"
                        value={reconciliationData.recoAmount}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Amount</option>
                        {recoAmounts.map((amt, idx) => (
                          <option key={idx} value={amt}>
                            {amt}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Reconciliation Currency <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="recoCurrency"
                        value={reconciliationData.recoCurrency}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Currency</option>
                        {currencies.map((currency) => (
                          <option
                            key={currency.currencyCodeId}
                            value={currency.currencyCode}
                          >
                            {currency.currencyCode} - {currency.currencyName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Transfer From Company Code <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="transferFromCompanyCode"
                        value={reconciliationData.transferFromCompanyCode}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Company Code</option>
                        {[...new Set(achdData.map((item) => item.doccc))].map(
                          (cc, idx) => (
                            <option key={idx} value={cc}>
                              {cc}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Transfer To Company Code <span className="text-red">**</span>
                      </Form.Label>
                      <Form.Select
                        name="transferToCompanyCode"
                        value={reconciliationData.transferToCompanyCode}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Company Code</option>
                        {[...new Set(achdData.map((item) => item.doccc))].map(
                          (cc, idx) => (
                            <option key={idx} value={cc}>
                              {cc}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Remarks</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="remarks"
                        value={reconciliationData.remarks}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Head 1</Form.Label>
                      <Form.Control
                        type="text"
                        name="head1"
                        value={reconciliationData.head1}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Head 2</Form.Label>
                      <Form.Control
                        type="text"
                        name="head2"
                        value={reconciliationData.head2}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Head 3</Form.Label>
                      <Form.Control
                        type="text"
                        name="head3"
                        value={reconciliationData.head3}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                </Row>

              </Accordion.Body>
            </Accordion.Item>

          </Accordion>

          <div className="fields-btn-container mt-4">
            <Button type="submit" className="fields-submit">
              Save Reconciliation Document
            </Button>
          </div>

        </Form>
      </div>

      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default ReconciliationDocument;